import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { ChevronDownIcon, XMarkIcon, MagnifyingGlassIcon, CheckIcon } from "@heroicons/react/24/outline";

const DROPDOWN_HEIGHT = 300;
const Z_INDEX = 2147483647;

const TableHeaderFilter = ({ label, options, selectedValues, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, openUpward: false });

  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let width = Math.max(rect.width, 240);
    let left = rect.left;

    if (left + width > viewportWidth - 8) left = viewportWidth - width - 8;
    if (left < 8) left = 8;

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < DROPDOWN_HEIGHT + 8 && spaceAbove > spaceBelow;
    const top = openUpward
      ? rect.top + window.scrollY - DROPDOWN_HEIGHT - 4
      : rect.bottom + window.scrollY + 4;

    setCoords({ top, left: left + window.scrollX, width, openUpward });
  };

  const toggleDropdown = () => {
    if (!open) updatePosition();
    setOpen((prev) => !prev);
    if (!open) setTimeout(() => searchRef.current?.focus(), 60);
  };

  useEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
        setSearch("");
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filteredOptions = useMemo(() => {
    return options.filter((opt) =>
      String(opt?.label ?? "").toLowerCase().includes(search.toLowerCase())
    );
  }, [options, search]);

  const handleSelect = (val) => {
    const next = selectedValues.includes(val)
      ? selectedValues.filter((v) => v !== val)
      : [...selectedValues, val];
    onChange(next);
  };

  const handleClear = () => {
    onChange([]);
    setSearch("");
  };

  const handleSelectAll = () => {
    if (selectedValues.length === filteredOptions.length) {
      onChange([]);
    } else {
      onChange(filteredOptions.map((o) => o.value));
    }
  };

  const dropdown = (
    <div
      ref={dropdownRef}
      style={{
        position: "absolute",
        top: coords.top,
        left: coords.left,
        width: coords.width,
        height: DROPDOWN_HEIGHT,
        zIndex: Z_INDEX,
      }}
      className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-200/80"
    >
      {/* Search bar */}
      <div className="p-2.5 border-b border-slate-100 bg-white flex-shrink-0">
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 focus-within:border-indigo-300 focus-within:bg-white transition-all">
          <MagnifyingGlassIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <input
            ref={searchRef}
            className="flex-1 text-sm text-slate-700 bg-transparent placeholder-slate-400 focus:outline-none min-w-0"
            placeholder={`Search ${label}…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button onClick={() => setSearch("")} className="text-slate-400 hover:text-slate-600 transition-colors">
              <XMarkIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Select all row */}
      {filteredOptions.length > 1 && (
        <div
          onClick={handleSelectAll}
          className="flex items-center gap-2.5 px-4 py-2 cursor-pointer text-xs font-bold text-indigo-500 hover:bg-indigo-50 border-b border-slate-100 flex-shrink-0 select-none transition-colors"
        >
          <div className={`w-4 h-4 rounded flex items-center justify-center border transition-all flex-shrink-0 ${
            selectedValues.length === filteredOptions.length && filteredOptions.length > 0
              ? "bg-indigo-500 border-indigo-500"
              : "border-slate-300"
          }`}>
            {selectedValues.length === filteredOptions.length && filteredOptions.length > 0 && (
              <CheckIcon className="w-2.5 h-2.5 text-white" />
            )}
          </div>
          <span>Select All {filteredOptions.length > 0 ? `(${filteredOptions.length})` : ""}</span>
        </div>
      )}

      {/* Options list */}
      <div className="flex-1 overflow-y-auto">
        <style>{`
          .filter-scroll::-webkit-scrollbar { width: 4px; }
          .filter-scroll::-webkit-scrollbar-track { background: transparent; }
          .filter-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 99px; }
        `}</style>
        <div className="filter-scroll h-full overflow-y-auto">
          {filteredOptions.length ? (
            filteredOptions.map((opt) => {
              const isSelected = selectedValues.includes(opt.value);
              return (
                <div
                  key={opt.value}
                  onClick={() => handleSelect(opt.value)}
                  className={`flex items-center gap-2.5 px-4 py-2.5 cursor-pointer text-sm select-none transition-colors ${
                    isSelected
                      ? "bg-indigo-50 text-indigo-700"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <div className={`w-4 h-4 rounded flex items-center justify-center border flex-shrink-0 transition-all ${
                    isSelected ? "bg-indigo-500 border-indigo-500" : "border-slate-300"
                  }`}>
                    {isSelected && <CheckIcon className="w-2.5 h-2.5 text-white" />}
                  </div>
                  <span className="truncate font-medium">{opt.label}</span>
                </div>
              );
            })
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-8 text-slate-400">
              <MagnifyingGlassIcon className="w-6 h-6 mb-2 opacity-50" />
              <span className="text-xs font-medium">No results found</span>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 px-3 py-2.5 border-t border-slate-100 bg-slate-50 flex items-center justify-between gap-2">
        <button
          onClick={handleClear}
          disabled={selectedValues.length === 0}
          className="flex items-center gap-1 text-xs font-semibold text-rose-500 hover:text-rose-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <XMarkIcon className="w-3.5 h-3.5" />
          Clear {selectedValues.length > 0 ? `(${selectedValues.length})` : ""}
        </button>
        <button
          onClick={() => { setOpen(false); setSearch(""); }}
          className="px-4 py-1.5 text-xs font-bold text-white bg-gradient-to-r from-indigo-500 to-violet-500 rounded-lg hover:from-indigo-600 hover:to-violet-600 transition-all shadow-sm shadow-indigo-200"
        >
          Apply
        </button>
      </div>
    </div>
  );

  const hasSelection = selectedValues.length > 0;

  return (
    <>
      <div
        ref={triggerRef}
        onClick={toggleDropdown}
        className={`inline-flex items-center gap-1.5 cursor-pointer px-2.5 py-1.5 rounded-lg transition-all select-none ${
          hasSelection
            ? "bg-indigo-100 text-indigo-700 border border-indigo-200"
            : "text-slate-500 hover:bg-slate-100 hover:text-slate-700 border border-transparent"
        }`}
      >
        <span className={`text-[13px] font-bold ${hasSelection ? "text-indigo-700" : "text-slate-600"}`}>
          {label}
        </span>

        <ChevronDownIcon
          className={`w-3 h-3 transition-transform duration-200 flex-shrink-0 ${
            open ? "rotate-180" : ""
          } ${hasSelection ? "text-indigo-500" : "text-slate-400"}`}
        />

        {hasSelection && (
          <span className="flex-shrink-0 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold bg-indigo-500 text-white rounded-full shadow-sm">
            {selectedValues.length}
          </span>
        )}
      </div>

      {open && createPortal(dropdown, document.body)}
    </>
  );
};

export default TableHeaderFilter;